import axios from 'axios';
import {
  hederaRestApiUrl,
  NetworkType,
  REACT_APP_BACKEND_URL,
} from '../utils/constants';
import { getMirrorBaseUrl } from '../utils/mirrorUrl';
import { getAuthAxiosInstance as authAxios } from './axios';
import { PrivateKey } from '@hashgraph/sdk';
import { Buffer } from 'buffer';

export const getAccountData = async (accountId: string, network: NetworkType) => {
  const baseUrl = getMirrorBaseUrl(network);
  const isCustom = network?.startsWith('custom-');

  // Custom networks route through backend proxy (needs auth header)
  const client = isCustom ? authAxios() : axios;
  if (!client) throw new Error('Not authenticated');

  const {
    data: {
      key: { key },
      balance: { balance },
    },
  } = await client.get(
    `${baseUrl}/api/v1/accounts/${accountId}`,
  );

  return {
    key,
    balance: balance / 100000000,
  };
};

export const getAccountDataByNetwork = async (accountId: string, network: NetworkType) => {
  const baseUrl = getMirrorBaseUrl(network);
  const isCustom = network?.startsWith('custom-');

  const client = isCustom ? authAxios() : axios;
  if (!client) throw new Error('Not authenticated');

  const { data } = await client.get(`${baseUrl}/api/v1/accounts/${accountId}`);
  return data;
};

/**
 * The bytes a wallet signs for hedera_signMessage, and what the backend
 * checks: the text behind a fixed prefix and its length.
 */
export const prefixLoginMessage = (message: string): Uint8Array =>
  Uint8Array.from(Buffer.from(`\x19Hedera Signed Message:\n${message.length}${message}`));

/** What proves possession of the account's key: a wallet's base64 SignatureMap, or a raw base64 signature. */
export type LoginProof = { signatureMap: string } | { signature: string };

/**
 * A new session token for `accountId`, by proving possession of its key: the
 * backend issues a text, `sign` signs it, and the backend checks that
 * signature against the account's key on Hedera. Stores nothing: the session
 * owner (hooks/useSession) decides whether the token is still wanted.
 */
export const requestSession = async (
  accountId: string,
  network: string,
  sign: (message: string) => Promise<LoginProof>,
): Promise<string> => {
  const {
    data: { message, challenge },
  } = await axios.post(`${REACT_APP_BACKEND_URL}/auth/challenge`, { accountId, network });
  const proof = await sign(message);
  const {
    data: { access_token },
  } = await axios.post(`${REACT_APP_BACKEND_URL}/auth/verify`, {
    accountId,
    network,
    challenge,
    ...proof,
  });
  return access_token;
};

/** Signs the backend's text with a decrypted local key (custom networks). */
export const signWithKey =
  (privateKey: Pick<PrivateKey, 'sign'>) =>
  async (message: string): Promise<LoginProof> => ({
    signature: Buffer.from(privateKey.sign(prefixLoginMessage(message))).toString('base64'),
  });
